import { memoize } from 'lodash';
import type { UUID } from './types.ts';

export function parseNumber(number: number | string) {
  if (number === undefined || number === null) return Number.NaN;
  return typeof number === 'number' ? number : Number.parseFloat(number.replace(',', '.'));
}

/**
 * Locale-free numeric rounding to a fixed number of decimal places.
 *
 * Use this instead of `parseNumber(formatNumber(value, n, n))`. `formatNumber` returns
 * locale-grouped strings (e.g. "1,234.57" in en-US, "1.234,57" in pt-BR) and `parseNumber`
 * only replaces a single comma with a dot before `parseFloat`, so any value >= 1000
 * collapses to ~1.234 once round-tripped through formatting + parsing.
 */
export const roundNumber = (value: number, decimals = 2): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/**
 * Using the logical OR operator (||) can cause issues with SonarCloud's TypeScript rule S6606,
 * if value exists or equal 0 and return boolean
 *
 * For more details, refer to:
 * https://sonarcloud.io/organizations/syngenta-digital/rules?open=typescript%3AS6606&rule_key=typescript%3AS6606
 *
 * If you need a comparison if value exists or is equal zero, consider using the nullish coalescing operator (??) instead.
 */
export const validateNumberOrZero = (value: number | undefined | null): boolean => {
  if (value === 0) return true;
  return !!value;
};

const noMemoizedHashNumber = (id: UUID): number => {
  const value = id.split('-')[0];
  return Number.parseInt(value, 16);
};

export const hashNumber = memoize(noMemoizedHashNumber);

/**
 * Using the logical OR operator (||) can cause issues with SonarCloud's TypeScript rule S6606,
 * as zero is considered a falsy value. This can lead to unintended behavior.
 *
 * For more details, refer to:
 * https://sonarcloud.io/organizations/syngenta-digital/rules?open=typescript%3AS6606&rule_key=typescript%3AS6606
 *
 * If the default value is zero, consider using the nullish coalescing operator (??) instead.
 */
export const getNumberOrDefault = (value: number | undefined | null, defaultValue: number): number => {
  if (value) {
    return value;
  }

  return defaultValue;
};
